import React, { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Box, Typography } from '@mui/material';

interface GroupMapProps {
  spatialSummary: Record<string, any>[];
  conditions: Record<string, number>;
  title?: string;
  height?: number;
}

function conditionColor(cond: number): string {
  if (cond >= 60) return '#43a047';
  if (cond >= 40) return '#fb8c00';
  if (cond >= 20) return '#e53935';
  return '#b71c1c';
}

function markerRadius(assetCount: number): number {
  return Math.max(4, Math.min(18, Math.sqrt(assetCount) * 0.8));
}

export default function GroupMap({
  spatialSummary,
  conditions,
  title,
  height = 380,
}: GroupMapProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const markersRef = useRef<Map<string, L.CircleMarker>>(new Map());

  // Create the map and markers once when spatialSummary changes
  useEffect(() => {
    if (!containerRef.current || spatialSummary.length === 0) return;

    if (mapRef.current) {
      mapRef.current.remove();
      mapRef.current = null;
      markersRef.current.clear();
    }

    const map = L.map(containerRef.current, { scrollWheelZoom: true });
    mapRef.current = map;

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors',
      maxZoom: 18,
    }).addTo(map);

    const bounds: [number, number][] = [];

    for (const g of spatialSummary) {
      const lat = Number(g.lat_center);
      const lng = Number(g.lon_center);
      if (isNaN(lat) || isNaN(lng)) continue;

      const groupId = String(g.group_id);
      const count = Number(g.asset_count) || 1;

      const circle = L.circleMarker([lat, lng], {
        radius: markerRadius(count),
        fillColor: '#90a4ae',
        color: '#fff',
        weight: 1.5,
        opacity: 0.9,
        fillOpacity: 0.75,
      });

      circle.addTo(map);
      markersRef.current.set(groupId, circle);
      bounds.push([lat, lng]);
    }

    if (bounds.length > 0) {
      map.fitBounds(bounds, { padding: [30, 30], maxZoom: 14 });
    }

    return () => {
      map.remove();
      mapRef.current = null;
      markersRef.current.clear();
    };
  }, [spatialSummary]);

  // Update marker colors and tooltips in-place when conditions change (preserves zoom/pan)
  useEffect(() => {
    for (const [groupId, marker] of Array.from(markersRef.current.entries())) {
      const cond = conditions[groupId] ?? 50;
      marker.setStyle({ fillColor: conditionColor(cond) });

      const g = spatialSummary.find(s => String(s.group_id) === groupId);
      const count = Number(g?.asset_count) || 1;
      marker.unbindTooltip();
      marker.bindTooltip(
        `<b>${groupId}</b><br/>Assets: ${count.toLocaleString()}<br/>Condition: ${cond.toFixed(
          0,
        )}`,
        { direction: 'top' },
      );
    }
  }, [conditions, spatialSummary]);

  if (!spatialSummary || spatialSummary.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="body2" color="text.secondary">
          No spatial data available
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {title && (
        <Typography
          variant="subtitle2"
          color="text.secondary"
          sx={{ mb: 0.5, textAlign: 'center' }}>
          {title}
        </Typography>
      )}
      <div
        ref={containerRef}
        style={{ height, borderRadius: 8, overflow: 'hidden' }}
      />
    </Box>
  );
}
